import { CheckCircle2, XCircle } from 'lucide-react'

import type { WorkflowConditionTraceStep } from '@/gen/cortado/v1/cortado_pb'
import { cn } from '@/lib/utils'

interface ConditionTraceTableProps {
  trace: WorkflowConditionTraceStep[]
  emptyText?: string
}

// formatExpected renders the IN operator's JSON-array-encoded expected value
// (e.g. '["a","b"]') as a readable comma-separated list. Falls back to the raw
// string for every other operator, and for IN values that fail to parse.
function formatExpected(step: WorkflowConditionTraceStep): string {
  if (step.op === 'IN') {
    try {
      const parsed = JSON.parse(step.expected)
      if (Array.isArray(parsed)) {
        return parsed.join(', ')
      }
    } catch {
      // fall through to the raw value
    }
  }
  return step.expected
}

export function ConditionTraceTable({
  trace,
  emptyText = 'No condition trace was recorded.',
}: ConditionTraceTableProps) {
  if (trace.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyText}</p>
  }

  return (
    <div className="overflow-x-auto rounded-md border bg-background">
      <table className="w-full min-w-[680px] text-xs">
        <thead>
          <tr className="border-b bg-muted/40 text-left text-muted-foreground">
            <th className="px-2 py-1.5 font-medium">Op</th>
            <th className="px-2 py-1.5 font-medium">Field</th>
            <th className="px-2 py-1.5 font-medium">Expected</th>
            <th className="px-2 py-1.5 font-medium">Actual</th>
            <th className="px-2 py-1.5 font-medium">Result</th>
            <th className="px-2 py-1.5 font-medium">Reason</th>
          </tr>
        </thead>
        <tbody>
          {trace.map((step, index) => (
            <tr key={`${step.op}-${step.field}-${index}`} className="border-b last:border-0">
              <td className="px-2 py-1.5 font-mono">{step.op || '-'}</td>
              <td className="px-2 py-1.5 font-mono">{step.field || '-'}</td>
              <td className="px-2 py-1.5">{formatExpected(step) || '-'}</td>
              <td className="px-2 py-1.5">{step.actual || '-'}</td>
              <td className="px-2 py-1.5">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 font-medium',
                    step.result
                      ? 'text-status-success-fg'
                      : 'text-status-danger-fg'
                  )}
                >
                  {step.result ? (
                    <CheckCircle2 className="size-3" />
                  ) : (
                    <XCircle className="size-3" />
                  )}
                  {step.result ? 'true' : 'false'}
                </span>
              </td>
              <td className="px-2 py-1.5 text-muted-foreground">{step.reason || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
